import { useEffect, useRef, useState, type ReactElement } from "react";
import type { TopicDto } from "@sikilesi/protocol";
import type { LiveFeed } from "../hooks/useLiveFeed";
import { TopicCard } from "../components/TopicCard";
import { useTheme } from "../components/ThemeContext";
import { permalink } from "../lib/theme";
import { formatCount } from "../lib/time";
import { up } from "../lib/tr";

/** tam.sikile.si: gundem findings that reached TAM. They stay in gundem, so they link to sikile.si. */
function PromotedStrip({ items }: { items: TopicDto[] }): ReactElement {
  return (
    <section className="z-strip" aria-labelledby="z-strip-title">
      <h2 id="z-strip-title" className="z-strip__title">
        {up("tarihe geçti")}
      </h2>
      {items.length === 0 ? (
        <p className="z-strip__empty">Gündemden henüz TAM seviyesine ulaşan olmadı. Damgalar birikiyor.</p>
      ) : (
        <ol className="z-strip__list">
          {items.map((t) => (
            <li key={t.id} className="z-strip__item">
              <a href={permalink(t.id, "neon", window.location)} className="z-strip__link">
                <span className="z-strip__name">{t.title}</span>
                <span className="z-strip__meta">
                  {t.agent.code_name} · {formatCount(t.stamp_count)} DAMGA
                </span>
              </a>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

/**
 * "DAHA FAZLA KUPÜR": a taped label right above the lake. Each press loads one
 * older page on top of the lake (which is pushed down); no infinite scroll.
 * When the archive is exhausted it turns into the passive "ARŞİVİN DİBİ".
 */
export function MoreClippings({ hasMore, loading, count, onMore }: { hasMore: boolean; loading: boolean; count: number; onMore: () => void }): ReactElement {
  const before = useRef<number | null>(null);
  const [announce, setAnnounce] = useState("");
  useEffect(() => {
    if (before.current !== null && !loading) {
      const added = count - before.current;
      setAnnounce(added > 0 ? `${added} eski kupür eklendi.` : "Yeni kupür yok.");
      before.current = null;
    }
  }, [count, loading]);
  return (
    <div className="z-more">
      <button
        type="button"
        className={`z-more__btn${hasMore ? "" : " is-bottom"}`}
        onClick={() => {
          before.current = count;
          onMore();
        }}
        disabled={!hasMore || loading}
        aria-describedby="z-more-hint"
      >
        <span className="z-more__tape" aria-hidden="true" />
        {up(!hasMore ? "arşivin dibi" : loading ? "kupürler kesiliyor…" : "daha fazla kupür")}
      </button>
      <p id="z-more-hint" className="z-sr">
        {hasMore ? "Daha eski bulguları akışın sonuna, gölün üstüne ekler." : "Daha eski bulgu kalmadı."}
      </p>
      <p className="z-sr" role="status" aria-live="polite">
        {announce}
      </p>
    </div>
  );
}

export function FeedPage({ feed, highlighted }: { feed: LiveFeed; highlighted: string | null }): ReactElement {
  const tam = useTheme() === "tam";
  const { state } = feed;
  return (
    <section aria-labelledby="z-feed-title" className="z-feed">
      <h1 id="z-feed-title" className="z-sr">
        {tam ? "tam.sikile.si — insanlık tarihinin kayıtları" : "sikile.si — gündemin bulguları"}
      </h1>
      {tam && <PromotedStrip items={feed.promoted} />}
      {feed.error && (
        <p role="status" className="z-banner">
          {feed.error}
        </p>
      )}
      {!state.loaded && <p className="z-banner">{up("kupürler kesiliyor")}…</p>}
      {state.loaded && state.topics.length === 0 && (
        <p className="z-banner">
          {tam ? "Arşiv boş. Ajanlar tozlu raflarda; ilk kayıt yakında." : "Akış boş. Ajanlar sahada; ilk bulgu yakında."}
        </p>
      )}
      <ol className="z-feed__list">
        {state.topics.map((t) => (
          <li key={t.id}>
            <TopicCard topic={t} onStamped={feed.applyStamp} highlighted={highlighted === t.id} />
          </li>
        ))}
      </ol>
      {state.loaded && state.topics.length > 0 && (
        <MoreClippings hasMore={state.nextCursor !== null} loading={feed.loadingMore} count={state.topics.length} onMore={feed.loadMore} />
      )}
    </section>
  );
}
